/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * Aplazame (APZ) checkout API: config, create checkout session.
 * Backend endpoints to be implemented in backend repo; uses existing API base URL and auth.
 */

const axios = require("axios")

/**
 * Get Aplazame iframe config (base URL for checkout iframe). Backend returns the URL for current environment.
 * @param {string} apiUrl - Base API URL from tunnelData.apiUrl (with or without trailing slash)
 * @param {string} accessToken - Bearer token from tunnelData.access_token
 * @returns {Promise<{ iframeBaseUrl: string }>}
 */
export async function getAplazameConfig(apiUrl, accessToken) {
  const baseUrl = (apiUrl || "").replace(/\/$/, "")
  const url = `${baseUrl}/middle-business-service/api/v2/aplazame-config`
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json"
  }
  const { data } = await axios.get(url, { headers })
  return {
    iframeBaseUrl: data.iframeBaseUrl
  }
}

/**
 * @typedef {{ checkoutId: string, publicKey?: string }} ApzCheckoutResult
 */

/**
 * Create an Aplazame checkout for the given transaction.
 * POST /middle-business-service/api/apz/checkout with JSON body { transactionId }.
 * Backend consumes application/json and accepts optional X-Tenant, X-Correlation-Id.
 * @param {string} transactionId - Order/transaction id (e.g. tunnelData.opl or tunnelData.data?.order?.id)
 * @param {string} apiUrl - Base API URL from tunnelData.apiUrl (with or without trailing slash)
 * @param {string} accessToken - Bearer token from tunnelData.access_token
 * @param {{ tenant?: string, correlationId?: string }} [options] - Optional headers X-Tenant, X-Correlation-Id when provided
 * @returns {Promise<ApzCheckoutResult>}
 */
export async function createCheckout(transactionId, apiUrl, accessToken, options = {}) {
  if (transactionId == null || String(transactionId).trim() === "") {
    throw new Error("transactionId is required for createCheckout")
  }
  const baseUrl = (apiUrl || "").replace(/\/$/, "")
  const url = `${baseUrl}/middle-business-service/api/apz/checkout`
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${accessToken}`
  }
  if (options.tenant != null && options.tenant !== "") {
    headers["X-Tenant"] = options.tenant
  }
  if (options.correlationId != null && options.correlationId !== "") {
    headers["X-Correlation-Id"] = options.correlationId
  }

  const body = { transactionId: String(transactionId).trim() }
  const { data } = await axios.post(url, body, { headers })
  return {
    checkoutId: data.checkoutId,
    publicKey: data.publicKey
  }
}
